'use client';
import { EnhancedSearchFilter } from '@/components/enhanced-search-filter';
import { SearchResultsHeader } from '@/components/search-results-header';
import { Button } from '@/components/ui/button';
import { UnifiedResultsList } from '@/components/unified-results-list';
import { useCurrentTenant } from '@/lib/tenant';
import { enhancedSearchQuestions } from '@/services/api/enhanced-search';
import type { QuestionInfoType } from '@/types/api/idea-starter';
import type { StoryInfoType } from '@/types/api/stories';
import { PlusCircle } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

interface EnhancedSearchFilters {
  contentType: 'all' | 'questions' | 'stories';
  questionTypes: string[];
  areaOfInterestIds: string[];
  categories: string[]; // Area of Interest names
  industryIds: string[];
  occupationIds: string[];
  tags: string[];
}

const SEARCH_LIMIT = 20;

export default function EnhancedSearchPage() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const urlSearchQuery = searchParams.get('q') || '';

  const { isAuthenticated, isLoading: authLoading } = useCurrentTenant();

  const [searchResults, setSearchResults] = useState<
    (QuestionInfoType | StoryInfoType)[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchSource, setSearchSource] = useState<
    'elasticsearch' | 'traditional'
  >('elasticsearch');
  const [sortBy, setSortBy] = useState('date');
  const [currentSearchQuery, setCurrentSearchQuery] = useState(urlSearchQuery);
  const [accumulatedSearchTerms, setAccumulatedSearchTerms] = useState<
    string[]
  >([]);

  const [selectedFilters, setSelectedFilters] = useState<EnhancedSearchFilters>(
    {
      contentType: 'all',
      questionTypes: [],
      areaOfInterestIds: [],
      categories: [],
      industryIds: [],
      occupationIds: [],
      tags: [],
    }
  );

  // Track if this is the initial render
  const isInitialRender = useRef(true);
  const lastUrlQuery = useRef(urlSearchQuery);

  // Minimal guards to prevent duplicate loops without changing behavior
  const inFlightSignatureRef = useRef<string | null>(null);
  const emptyRetrySignaturesRef = useRef<Set<string>>(new Set());

  const buildRequestSignature = (
    searchQuery: string,
    page: number,
    filters: EnhancedSearchFilters,
    sort: string
  ): string => {
    const normalized: EnhancedSearchFilters = {
      contentType: filters.contentType,
      questionTypes: [...filters.questionTypes].sort(),
      areaOfInterestIds: [...filters.areaOfInterestIds].sort(),
      categories: [...(filters.categories || [])].sort(),
      industryIds: [...filters.industryIds].sort(),
      occupationIds: [...filters.occupationIds].sort(),
      tags: [...filters.tags].sort(),
    };
    return JSON.stringify({
      q: (searchQuery || '').trim(),
      page,
      filters: normalized,
      sort,
    });
  };

  // ✅ NEW: Handle URL changes
  useEffect(() => {
    setCurrentSearchQuery(urlSearchQuery);

    // Initialize accumulated search terms from URL query
    if (urlSearchQuery && urlSearchQuery.trim()) {
      const terms = urlSearchQuery
        .trim()
        .split(/\s+/)
        .filter((term) => term.length > 0);
      setAccumulatedSearchTerms(terms);
    } else {
      setAccumulatedSearchTerms([]);
    }

    // 🔍 SEARCH PAGE LOG: Track URL parameter changes
    console.log('🔍 SEARCH PAGE - URL parameters changed:', {
      urlSearchQuery,
      urlAreaOfInterestIds: selectedFilters.areaOfInterestIds,
      urlContentType: selectedFilters.contentType,
      lastUrlQuery: lastUrlQuery.current,
      timestamp: new Date().toISOString(),
    });

    // ✅ FIXED: Always perform search on URL change, including empty queries
    // This ensures both page reload and tag removal work consistently
    if (
      urlSearchQuery !== lastUrlQuery.current ||
      (urlSearchQuery === '' &&
        lastUrlQuery.current === '' &&
        isInitialRender.current)
    ) {
      // ✅ IMPROVED: Add small delay to ensure authentication state is ready
      setTimeout(() => {
        performSearch(urlSearchQuery, 1, true);
      }, 100);
      lastUrlQuery.current = urlSearchQuery;
    }
  }, [urlSearchQuery]);

  // ✅ NEW: Handle sort changes by re-executing search
  useEffect(() => {
    if (!isInitialRender.current) {
      // 🔍 SEARCH PAGE LOG: Track sort changes
      console.log('🔍 SEARCH PAGE - Sort changed:', {
        sortBy,
        currentSearchQuery,
        timestamp: new Date().toISOString(),
      });

      // Re-execute search when sort changes (but not on initial render)
      performSearch(currentSearchQuery, 1, true);
    }
  }, [sortBy]);

  // ✅ UPDATED: Handle both search queries and "view all" functionality
  const performSearch = async (
    searchQuery: string,
    page: number = 1,
    resetResults: boolean = false
  ) => {
    const signature = buildRequestSignature(
      searchQuery,
      page,
      selectedFilters,
      sortBy
    );

    // Dedupe identical in-flight requests for the same signature
    if (inFlightSignatureRef.current === signature) {
      console.log(
        '🔁 DEDUPE DEBUG - Skipping duplicate in-flight search for signature'
      );
      return;
    }

    // Allow search for both authenticated and non-authenticated users
    if (authLoading) {
      return;
    }

    inFlightSignatureRef.current = signature;

    // 🔍 NEW LOG: Track empty query processing
    if (!searchQuery.trim()) {
      console.log(
        '🔍 EMPTY QUERY PROCESSING DEBUG - Processing empty search query:',
        {
          searchQuery,
          searchQueryLength: searchQuery.length,
          searchQueryTrimmed: searchQuery.trim(),
          page,
          resetResults,
          sortBy,
          selectedFilters,
          timestamp: new Date().toISOString(),
        }
      );
    }

    // 🔍 SEARCH PAGE LOG: Track search execution
    console.log('🔍 SEARCH PAGE - Search execution started:', {
      searchQuery,
      page,
      resetResults,
      sortBy,
      selectedFilters,
      timestamp: new Date().toISOString(),
    });

    if (resetResults) {
      setIsLoading(true);
    } else {
      setIsLoadingNextPage(true);
    }
    try {
      // Use the same approach as navbar search for better results
      const searchPayload = {
        search: searchQuery.trim() || '', // ✅ FIXED: Send empty string instead of undefined for trending results
        page,
        limit: SEARCH_LIMIT,
        sortBy: (searchQuery.trim() ? sortBy : 'trending') as
          | 'relevance'
          | 'date'
          | 'trending',
        // Only include filters if they are actually set
        ...(selectedFilters.tags.length > 0 && {
          tags: selectedFilters.tags,
        }),

        ...(selectedFilters.contentType !== 'all' && {
          type: (selectedFilters.contentType === 'questions'
            ? 'question'
            : 'story') as 'question' | 'story',
        }),
        ...(selectedFilters.questionTypes.length > 0 && {
          questionTypes: selectedFilters.questionTypes,
        }),
        ...(selectedFilters.categories.length > 0 && {
          categories: selectedFilters.categories,
        }),
        ...(selectedFilters.areaOfInterestIds.length > 0 && {
          areaOfInterestIds: selectedFilters.areaOfInterestIds,
        }),
        ...(selectedFilters.industryIds.length > 0 && {
          industryIds: selectedFilters.industryIds,
        }),
        ...(selectedFilters.occupationIds.length > 0 && {
          occupationIds: selectedFilters.occupationIds,
        }),
      };

      // 🔎 EXACT-PHRASE DEBUG & CONTENT-TYPE DEBUG (no behavior change)
      try {
        console.log('🔎 EXACT-PHRASE DEBUG', {
          q: searchQuery,
          qLength: searchQuery.length,
          looksExact:
            searchQuery.split(/\s+/).length >= 5 || /[?.!"]$/.test(searchQuery),
        });
        console.log('🔎 CONTENT-TYPE DEBUG', {
          selectedContentType: selectedFilters.contentType,
          payloadHasType: Object.prototype.hasOwnProperty.call(
            searchPayload,
            'type'
          ),
          payloadType: (searchPayload as any).type ?? null,
          otherFilters: {
            questionTypes: selectedFilters.questionTypes,
            categories: selectedFilters.categories,
            areaOfInterestIds: selectedFilters.areaOfInterestIds,
            industryIds: selectedFilters.industryIds,
            occupationIds: selectedFilters.occupationIds,
            tags: selectedFilters.tags,
          },
        });
      } catch (e) {
        // swallow logging errors safely
      }

      // Performance optimization: Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 SEARCH DEBUG - Search payload:', {
          searchQuery,
          page,
          sortBy,
        });
      }

      const response = await enhancedSearchQuestions(searchPayload);

      // 🔍 SEARCH PAGE LOG: Track search response
      console.log('🔍 SEARCH PAGE - Search response received:', {
        searchQuery,
        page,
        resultCount: response.data.length,
        totalCount: response.totalCount,
        searchSource: response.source,
        hasResults: response.data.length > 0,
        timestamp: new Date().toISOString(),
      });

      // ⚠️ Detect empty results specifically for contentType=all with a non-empty query
      if (
        response.data.length === 0 &&
        selectedFilters.contentType === 'all' &&
        searchQuery.trim()
      ) {
        console.warn(
          '⚠️ CONTENT-TYPE ANOMALY - Empty results with contentType=all; backend may treat missing `type` incorrectly.',
          {
            payloadHasType: Object.prototype.hasOwnProperty.call(
              searchPayload,
              'type'
            ),
            payloadType: (searchPayload as any).type ?? null,
            payload: searchPayload,
          }
        );

        // Dev-only diagnostics: issue per-type queries to log counts (no UI state changes)
        if (process.env.NODE_ENV === 'development') {
          try {
            const [qRes, sRes] = await Promise.all([
              enhancedSearchQuestions({ ...searchPayload, type: 'question' }),
              enhancedSearchQuestions({ ...searchPayload, type: 'story' }),
            ]);
            console.log('🔬 DIAG - per-type counts', {
              qCount: qRes.data.length,
              sCount: sRes.data.length,
              totalCountQ: qRes.totalCount,
              totalCountS: sRes.totalCount,
            });
          } catch (diagErr) {
            console.log('🔬 DIAG - per-type failed', diagErr);
          }
        }
      }

      // 🔍 DEBUG: Log empty search query handling
      console.log('🔍 EMPTY SEARCH DEBUG - Search payload:', {
        searchQuery: searchQuery,
        searchQueryTrimmed: searchQuery.trim(),
        searchQueryIsEmpty: !searchQuery.trim(),
        page: page,
        resetResults: resetResults,
        sortBy: sortBy,
        searchPayload: searchPayload,
      });

      // 🔍 DEBUG: Log the search payload for area of interest filters
      console.log('🔍 SEARCH DEBUG - Area of Interest Filter Data:', {
        selectedFilters: selectedFilters,
        areaOfInterestIds: selectedFilters.areaOfInterestIds,
        areaOfInterestIdsType: typeof selectedFilters.areaOfInterestIds[0],
        searchPayload: searchPayload,
        hasAreaOfInterestFilter: selectedFilters.areaOfInterestIds.length > 0,
        searchPayloadKeys: Object.keys(searchPayload),
      });

      // 🔍 DEBUG: Check if API is returning the same data for empty queries
      if (!searchQuery.trim() && page > 1) {
        console.log(
          '🔍 EMPTY QUERY PAGINATION DEBUG - Checking for API pagination issues'
        );
        console.log(
          '🔍 EMPTY QUERY PAGINATION DEBUG - Page:',
          page,
          'Results:',
          response.data.length
        );
      }

      // 🔍 DEBUG: Check if API is returning different data for different pages
      console.log('🔍 PAGINATION DEBUG - API Response:', {
        page: page,
        searchQueryIsEmpty: !searchQuery.trim(),
        totalCount: response.totalCount,
        resultCount: response.data.length,
        firstItemId: response.data[0]?.id,
        lastItemId: response.data[response.data.length - 1]?.id,
        allItemIds: response.data.map((item) => item.id),
      });

      // 🔍 DEBUG: Log search results to check if filtering is working
      console.log('🔍 RESULTS DEBUG - Search Response:', {
        totalCount: response.totalCount,
        resultCount: response.data.length,
        source: response.source,
        appliedFilter: selectedFilters.areaOfInterestIds,
        firstFewResults: response.data.slice(0, 3).map((item) => {
          const isStory = 'type' in item && item.type === 'story';
          return {
            id: item.id,
            type: isStory ? 'story' : 'question',
            title: isStory
              ? (item as any).headline
              : (item as any).questionTitle,
            category: (item as any).category,
            categoryId: (item as any).category?.id,
            categoryName: (item as any).category?.name,
          };
        }),
        allResultsCategories: response.data.map((item) => {
          const isStory = 'type' in item && item.type === 'story';
          return {
            id: item.id,
            type: isStory ? 'story' : 'question',
            categoryId: (item as any).category?.id,
            categoryName: (item as any).category?.name,
          };
        }),
      });

      // 🔍 DEBUG: Check if any results match the applied filter
      const matchingResults = response.data.filter((item) => {
        const categoryId = (item as any).category?.id;
        return selectedFilters.areaOfInterestIds.includes(categoryId);
      });

      console.log('🔍 FILTER MATCH DEBUG:', {
        appliedFilter: selectedFilters.areaOfInterestIds,
        totalResults: response.data.length,
        matchingResults: matchingResults.length,
        matchingResultIds: matchingResults.map((item) => ({
          id: item.id,
          categoryId: (item as any).category?.id,
          categoryName: (item as any).category?.name,
        })),
      });

      if (resetResults) {
        setSearchResults(response.data);
        setTotalCount(response.totalCount); // Only update total count on first page
        console.log('🔍 SEARCH DEBUG - Reset results:', {
          newResultsCount: response.data.length,
          totalCount: response.totalCount,
          page: page,
        });
      } else {
        setSearchResults((prev) => {
          // Check for duplicates
          const existingIds = new Set(prev.map((item) => item.id));
          const newItems = response.data.filter(
            (item) => !existingIds.has(item.id)
          );

          console.log('🔍 SEARCH DEBUG - Appending results:', {
            previousCount: prev.length,
            newResultsCount: response.data.length,
            uniqueNewResults: newItems.length,
            totalAfterAppend: prev.length + newItems.length,
            page: page,
            hasDuplicates: newItems.length !== response.data.length,
            searchQueryIsEmpty: !searchQuery.trim(),
            firstFewNewItemIds: newItems.slice(0, 3).map((item) => item.id),
            previousItemIds: prev.slice(-5).map((item) => item.id), // Last 5 items from previous
            newItemIds: response.data.slice(0, 5).map((item) => item.id), // First 5 items from new response
            // 🔍 DEBUG: Track type distribution in new data
            newDataTypes: response.data.map((item) => {
              const isStory = 'type' in item && item.type === 'story';
              return { id: item.id, type: isStory ? 'story' : 'question' };
            }),
            uniqueNewDataTypes: newItems.map((item) => {
              const isStory = 'type' in item && item.type === 'story';
              return { id: item.id, type: isStory ? 'story' : 'question' };
            }),
          });

          return [...prev, ...newItems];
        });
      }

      setCurrentPage(page);
    } catch (error) {
      console.error('🔍 SEARCH ERROR - Search failed:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingNextPage(false);
      // Clear in-flight guard
      inFlightSignatureRef.current = null;
    }
  };

  // ✅ FIXED: Initialize search from URL parameter with proper state management
  useEffect(() => {
    console.log('🔍 URL CHANGE DEBUG - URL Search Query Changed:', {
      urlSearchQuery,
      currentSearchQuery,
      lastUrlQuery: lastUrlQuery.current,
      isInitialRender: isInitialRender.current,
    });

    // Check if URL query actually changed
    if (urlSearchQuery !== lastUrlQuery.current) {
      console.log('🔍 URL CHANGE DEBUG - URL query changed, updating state');
      lastUrlQuery.current = urlSearchQuery;
      setCurrentSearchQuery(urlSearchQuery);

      // Perform search for the new query or on initial render
      if (urlSearchQuery || isInitialRender.current) {
        console.log(
          '🔍 URL CHANGE DEBUG - Performing search with query:',
          urlSearchQuery
        );
        performSearch(urlSearchQuery, 1, true);
      }
    }
  }, [urlSearchQuery]);

  // ✅ NEW: Handle browser back/forward navigation events and URL synchronization
  useEffect(() => {
    const handlePopState = () => {
      // Force a re-read of the URL parameters when browser navigation occurs
      const currentUrlQuery = searchParams.get('q') || '';
      console.log('🔍 POPSTATE DEBUG - Browser navigation detected:', {
        currentUrlQuery,
        previousQuery: currentSearchQuery,
      });

      if (currentUrlQuery !== currentSearchQuery) {
        console.log(
          '🔍 POPSTATE DEBUG - Updating search query from browser navigation'
        );
        setCurrentSearchQuery(currentUrlQuery);
        performSearch(currentUrlQuery, 1, true);
      }
    };

    // Listen for browser navigation events
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [searchParams, currentSearchQuery]);

  // ✅ NEW: Additional URL monitoring for better reliability
  useEffect(() => {
    // Check if URL query differs from current state (fallback mechanism)
    const urlQuery = searchParams.get('q') || '';
    if (urlQuery !== currentSearchQuery && (urlQuery || currentSearchQuery)) {
      console.log(
        '🔍 URL SYNC DEBUG - URL query differs from state, syncing:',
        {
          urlQuery,
          currentSearchQuery,
        }
      );
      setCurrentSearchQuery(urlQuery);
      performSearch(urlQuery, 1, true);
    }
  }, [searchParams, currentSearchQuery]);

  // ✅ FIXED: Ensure proper initialization on component mount
  useEffect(() => {
    // On initial mount, always perform a search (even for empty queries)
    if (isInitialRender.current) {
      console.log(
        '🔍 INIT DEBUG - Initial mount with URL query:',
        urlSearchQuery
      );

      // 🔍 NEW LOG: Track empty query initialization
      if (!urlSearchQuery.trim()) {
        console.log(
          '🔍 EMPTY QUERY INIT DEBUG - Initializing search page with empty query:',
          {
            urlSearchQuery,
            urlSearchQueryLength: urlSearchQuery.length,
            urlSearchQueryTrimmed: urlSearchQuery.trim(),
            willPerformSearch: true,
            timestamp: new Date().toISOString(),
          }
        );
      }

      setCurrentSearchQuery(urlSearchQuery);
      // ✅ FIXED: Don't call performSearch here - let the URL change effect handle it
      // This prevents duplicate searches and ensures proper authentication timing
      isInitialRender.current = false;
    }
  }, [urlSearchQuery]);

  // ✅ FIXED: Handle filter changes - removed restrictive condition
  useEffect(() => {
    // 🔍 DEBUG: Log when useEffect is triggered for filter changes
    console.log('🔍 USEEFFECT DEBUG - Filter Change Triggered:', {
      selectedFilters: selectedFilters,
      currentSearchQuery: currentSearchQuery,
      areaOfInterestIds: selectedFilters.areaOfInterestIds,
      isInitialRender: isInitialRender.current,
    });

    // Skip the very first render to prevent conflicts with URL-based search
    if (isInitialRender.current) {
      isInitialRender.current = false;
      console.log('🔍 USEEFFECT DEBUG - Skipping initial render');
      return;
    }

    // Always trigger search for filter changes, regardless of URL query match
    performSearch(currentSearchQuery, 1, true);
  }, [selectedFilters]);

  // ✅ NEW: Retry search when authentication state changes (works for both authenticated and non-authenticated users)
  useEffect(() => {
    // Retry once per signature when authentication completes, to avoid loops on empty results
    if (!authLoading && searchResults.length === 0 && !isLoading) {
      const sig = buildRequestSignature(
        currentSearchQuery,
        1,
        selectedFilters,
        sortBy
      );
      if (!emptyRetrySignaturesRef.current.has(sig)) {
        emptyRetrySignaturesRef.current.add(sig);
        console.log('🔍 AUTH RETRY DEBUG - Retrying once for signature');
        performSearch(currentSearchQuery, 1, true);
      } else {
        console.log(
          '🔍 AUTH RETRY DEBUG - Skip retry, already attempted for signature'
        );
      }
    }
  }, [isAuthenticated, authLoading]);

  // ✅ NEW: Ensure search is performed when authentication is ready and no search has been done yet (works for both authenticated and non-authenticated users)
  useEffect(() => {
    if (
      !authLoading &&
      searchResults.length === 0 &&
      !isLoading &&
      !isInitialRender.current
    ) {
      const sig = buildRequestSignature(
        currentSearchQuery,
        1,
        selectedFilters,
        sortBy
      );
      if (!emptyRetrySignaturesRef.current.has(sig)) {
        emptyRetrySignaturesRef.current.add(sig);
        console.log(
          '🔍 AUTH READY DEBUG - Performing initial search once for signature'
        );
        performSearch(currentSearchQuery, 1, true);
      } else {
        console.log(
          '🔍 AUTH READY DEBUG - Initial search already attempted for signature'
        );
      }
    }
  }, [isAuthenticated, authLoading, searchResults.length, isLoading]);

  const questionResults = searchResults.filter(
    (result) => !('type' in result) || (result as any).type !== 'story'
  ) as QuestionInfoType[];

  const storyResults = searchResults.filter(
    (result) => 'type' in result && (result as any).type === 'story'
  ) as (StoryInfoType & { type: 'story' })[];

  const questionCount = questionResults.length;
  const storyCount = storyResults.length;

  if (authLoading) {
    return (
      <section className="p-8 max-w-6xl w-full mx-auto">
        <div className="text-center text-sm text-gray-500">
          Loading search...
        </div>
      </section>
    );
  }

  return (
    <section className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 lg:gap-12 p-4 lg:p-8 max-w-6xl w-full mx-auto">
      {/* Filters Sidebar */}
      <div className="lg:sticky lg:top-8 h-fit order-2 lg:order-1">
        <EnhancedSearchFilter
          selectedFilters={selectedFilters}
          setSelectedFilters={(newFilters) => {
            setSelectedFilters(newFilters);
          }}
        />
      </div>

      <div className="flex flex-col gap-4 order-1 lg:order-2 min-w-0">
        <SearchResultsHeader
          searchQuery={currentSearchQuery}
          totalCount={totalCount}
          questionCount={questionCount}
          storyCount={storyCount}
          searchSource={searchSource}
          sortBy={sortBy}
          onSortChange={setSortBy}
          isLoading={isLoading}
        />

        <UnifiedResultsList
          questions={questionResults}
          stories={storyResults}
          isLoading={isLoading}
          isInfiniteScroll={true}
          hasNextPage={currentPage * SEARCH_LIMIT < totalCount}
          isFetchingNextPage={isLoadingNextPage}
          fetchNextPage={() => {
            console.log('🔍 INFINITE SCROLL DEBUG - fetchNextPage called:', {
              currentPage,
              totalCount,
              searchLimit: SEARCH_LIMIT,
              hasMorePages: currentPage * SEARCH_LIMIT < totalCount,
              currentResultsCount: searchResults.length,
              isLoadingNextPage,
              searchQueryIsEmpty: !currentSearchQuery.trim(),
            });

            // For empty search queries, check if we've already loaded all available results
            if (
              !currentSearchQuery.trim() &&
              searchResults.length >= totalCount
            ) {
              console.log(
                '🔍 EMPTY SEARCH DEBUG - Already loaded all results, stopping pagination'
              );
              return;
            }

            // For empty search queries, if we're getting duplicates, try a different approach
            if (!currentSearchQuery.trim() && currentPage > 1) {
              // Check if the last page returned the same data as the first page
              const firstPageIds = searchResults
                .slice(0, SEARCH_LIMIT)
                .map((item) => item.id);
              const lastPageIds = searchResults
                .slice(-SEARCH_LIMIT)
                .map((item) => item.id);
              const isDuplicatePage =
                firstPageIds.length === lastPageIds.length &&
                firstPageIds.every((id, index) => id === lastPageIds[index]);

              if (isDuplicatePage) {
                console.warn(
                  '🔍 EMPTY SEARCH DEBUG - Detected duplicate page data, API may not be paginating correctly'
                );
                console.warn(
                  '🔍 EMPTY SEARCH DEBUG - Stopping pagination to prevent infinite duplicates'
                );
                return;
              }
            }

            if (currentPage * SEARCH_LIMIT < totalCount && !isLoadingNextPage) {
              console.log(
                '🔍 INFINITE SCROLL DEBUG - Loading next page:',
                currentPage + 1
              );
              performSearch(currentSearchQuery, currentPage + 1, false);
            } else {
              console.log(
                '🔍 INFINITE SCROLL DEBUG - No more pages to load or already loading'
              );
            }
          }}
          showSearchComponent={true}
          initialSearchQuery={currentSearchQuery}
          initialFilters={selectedFilters}
          onSearch={(query, filters) => {
            // Update the selected filters when search is performed
            setSelectedFilters(filters);

            // Handle accumulated search terms properly
            const trimmedQuery = query.trim();
            if (trimmedQuery) {
              // Split the incoming query into terms
              const incomingTerms = trimmedQuery
                .split(/\s+/)
                .filter((term) => term.length > 0);

              // Update accumulated search terms
              setAccumulatedSearchTerms(incomingTerms);

              // Update the current search query to the accumulated terms
              const combinedQuery = incomingTerms.join(' ');
              setCurrentSearchQuery(combinedQuery);

              // Update URL to reflect the new search query
              const searchParams = new URLSearchParams();
              searchParams.set('q', combinedQuery);
              router.push(`/search?${searchParams.toString()}`);

              // Perform the search with the combined query
              performSearch(combinedQuery, 1, true);
            } else {
              // Clear search terms if empty query
              setAccumulatedSearchTerms([]);
              setCurrentSearchQuery('');

              // Clear URL query parameter
              router.push('/search');

              performSearch('', 1, true);
            }
          }}
        />
        {searchResults.length === 0 && !isLoading && (
          <div className="flex items-center justify-center p-20">
            <div className="text-center">
              {currentSearchQuery.trim() ? (
                <div className="flex flex-col gap-4">
                  <div className="">
                    <p className="text-sm text-gray-500 mb-2">
                      No results found for &ldquo;{currentSearchQuery}&rdquo;.
                    </p>
                    <p className="text-sm text-gray-500">
                      Try another keyword or add a new question to start the
                      discussion.
                    </p>
                  </div>

                  <Button
                    onClick={() => {
                      router.push('/ask-question');
                    }}
                    variant="outline"
                    className="flex items-center gap-2 font-semibold text-ihs-blue border-ihs-blue hover:bg-ihs-blue/10"
                  >
                    <PlusCircle className="w-4 h-4 text-ihs-blue" />
                    Add Question
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-2">
                    No content available
                  </p>
                  <p className="text-xs text-gray-400">
                    Try adjusting your filters or check back later
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
